import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Phone } from "lucide-react";
import { FAQS } from "@/lib/faqs";
import { COMPANY } from "@/lib/constants";

export const FAQ = ({ faqs = FAQS, heading = "Answers,", accent = "straight." }) => {
  return (
    <section
      id="faq"
      className="relative py-24 sm:py-32 bg-[#0B1726] border-y border-slate-900"
      data-testid="faq-section"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#1E88E5]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445]">
                FAQ
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white mb-6">
              {heading} <br />
              <span className="text-[#E6B445]">{accent}</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8">
              Quick answers about pricing, service areas, methods and scheduling.
              Still curious? Call or text the number below — we're standing by.
            </p>
            <a
              href={COMPANY.phoneHref}
              data-testid="faq-call-btn"
              className="inline-flex items-center gap-3 bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-bold tracking-wider uppercase px-6 py-3 rounded-sm text-sm transition-all"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
          </div>

          <div className="lg:col-span-8">
            <Accordion
              type="single"
              collapsible
              className="w-full space-y-3"
              defaultValue="item-0"
              data-testid="faq-accordion"
            >
              {faqs.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                >
                  <AccordionItem
                    value={`item-${i}`}
                    className="bg-[#142238] border border-slate-800 rounded-md px-5 data-[state=open]:border-[#1E88E5]/60"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5 group">
                      <div className="flex items-start gap-3 pr-4">
                        <HelpCircle className="w-5 h-5 text-[#E6B445] flex-shrink-0 mt-0.5" />
                        <span className="font-heading text-base sm:text-lg font-bold uppercase tracking-tight text-white group-hover:text-[#E6B445] transition-colors">
                          {f.q}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-300 leading-relaxed pb-5 pl-8 text-[15px]">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
